import React, { useState } from 'react';

import { GetCEPInfoAPI } from '../../api/API';
import { maskCep, maskCpf, maskDate, maskPhone, onlyDigits } from './registrationMasks';

const EMPTY_FORM = {
    name: '',
    countryCode: '55',
    phone: '',
    cpf: '',
    birthday: '',
    gender: '99',
    email: '',
    cep: '',
    address: '',
    number: '',
    complement: '',
    district: '',
    city: '',
    state: '',
    financial_responsible_name: '',
    financial_responsible_relationship: '',
    financial_responsible_document: '',
    financial_responsible_phone: '',
    financial_responsible_email: '',
};

const MASKS = {
    phone: maskPhone,
    cpf: maskCpf,
    birthday: maskDate,
    cep: maskCep,
    financial_responsible_document: maskCpf,
    financial_responsible_phone: maskPhone,
    countryCode: (v) => onlyDigits(v).slice(0, 3),
    state: (v) => String(v || '').toUpperCase().slice(0, 2),
};

/**
 * Formulário que o paciente preenche pelo link de cadastro.
 * Mesmos campos do cadastro feito pela clínica (CreatePatientPopup), sem os
 * internos (origem, canal, campanha). A validação final é do backend; os
 * erros por campo voltam em `errors`.
 */
export default function PatientRegistrationForm({ clinicName, submitting, errors, generalError, onSubmit }) {
    const [form, setForm] = useState(EMPTY_FORM);
    const [hasResponsible, setHasResponsible] = useState(false);

    const set = (key) => (e) => {
        const mask = MASKS[key];
        const value = mask ? mask(e.target.value) : e.target.value;
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const lookupCep = () => {
        const cep = onlyDigits(form.cep);
        if (cep.length !== 8) return;
        GetCEPInfoAPI(cep, (result) => {
            if (!result || result.erro || result.error) return;
            setForm((prev) => ({
                ...prev,
                address: result.logradouro || prev.address,
                district: result.bairro || prev.district,
                city: result.localidade || prev.city,
                state: result.uf || prev.state,
            }));
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submitting) return;
        const data = { ...form, has_responsible: hasResponsible ? '1' : '' };
        if (!hasResponsible) {
            Object.keys(data)
                .filter((key) => key.startsWith('financial_responsible_'))
                .forEach((key) => { data[key] = ''; });
        }
        onSubmit(data);
    };

    const field = (key, label, props = {}) => (
        <div className="ps-form__group">
            <label className="ps-form__label" htmlFor={`ppr-${key}`}>{label}</label>
            <input
                id={`ppr-${key}`}
                className={`ps-form__input${errors[key] ? ' ppr-input--error' : ''}`}
                value={form[key]}
                onChange={set(key)}
                {...props}
            />
            {errors[key] && <span className="ppr-error">{errors[key]}</span>}
        </div>
    );

    return (
        <form className="ps-step-content" onSubmit={handleSubmit} noValidate>
            <h2 className="ps-step-title">Cadastro do paciente</h2>
            <p className="ps-step-subtitle">
                Preencha com os dados de quem será atendido.
                {' '}Se você está preenchendo por outra pessoa, como um filho, os seus dados entram no final, como responsável.
                {' '}Campos com * são obrigatórios.
            </p>

            {generalError && <div className="ppr-banner-error" role="alert">{generalError}</div>}

            <div className="ps-form-card">
                <h3 className="ppr-section-title">Dados do paciente</h3>
                {field('name', 'Nome completo do paciente *', { autoComplete: 'name', maxLength: 150 })}
                <div className="ppr-row ppr-row--ddi">
                    {field('countryCode', 'DDI', { inputMode: 'numeric' })}
                    {field('phone', 'WhatsApp com DDD *', { inputMode: 'tel', autoComplete: 'tel-national', placeholder: '(11) 91234-5678' })}
                </div>
                <div className="ppr-row">
                    {field('cpf', 'CPF', { inputMode: 'numeric', placeholder: '000.000.000-00' })}
                    {field('birthday', 'Nascimento', { inputMode: 'numeric', placeholder: 'dd/mm/aaaa', autoComplete: 'bday' })}
                </div>
                <div className="ps-form__group">
                    <label className="ps-form__label" htmlFor="ppr-gender">Sexo</label>
                    <select id="ppr-gender" className="ps-form__input" value={form.gender} onChange={set('gender')}>
                        <option value="99">Prefiro não informar</option>
                        <option value="1">Feminino</option>
                        <option value="0">Masculino</option>
                    </select>
                </div>
                {field('email', 'E-mail', { type: 'email', inputMode: 'email', autoComplete: 'email', maxLength: 150 })}
            </div>

            <div className="ps-form-card">
                <h3 className="ppr-section-title">Endereço do paciente</h3>
                {field('cep', 'CEP', { inputMode: 'numeric', autoComplete: 'postal-code', placeholder: '00000-000', onBlur: lookupCep })}
                {field('address', 'Rua', { autoComplete: 'address-line1', maxLength: 200 })}
                <div className="ppr-row ppr-row--number">
                    {field('number', 'Número', { inputMode: 'numeric' })}
                    {field('complement', 'Complemento', { autoComplete: 'address-line2', maxLength: 100 })}
                </div>
                {field('district', 'Bairro', { maxLength: 100 })}
                <div className="ppr-row ppr-row--city">
                    {field('city', 'Cidade', { autoComplete: 'address-level2', maxLength: 100 })}
                    {field('state', 'UF', { autoComplete: 'address-level1' })}
                </div>
            </div>

            <div className="ps-form-card">
                <label className="ppr-toggle">
                    <input type="checkbox" checked={hasResponsible} onChange={(e) => setHasResponsible(e.target.checked)} />
                    <span>
                        O paciente é menor de idade ou dependente?
                        <span className="ppr-toggle__hint">Marque para cadastrar os dados do responsável.</span>
                    </span>
                </label>
                {hasResponsible && (
                    <div className="ppr-responsible">
                        <h3 className="ppr-section-title">Dados do responsável</h3>
                        {field('financial_responsible_name', 'Nome do responsável *', { maxLength: 255 })}
                        {field('financial_responsible_relationship', 'Parentesco', { placeholder: 'Mãe, pai, cônjuge...', maxLength: 100 })}
                        <div className="ppr-row">
                            {field('financial_responsible_document', 'CPF do responsável', { inputMode: 'numeric' })}
                            {field('financial_responsible_phone', 'Telefone', { inputMode: 'tel' })}
                        </div>
                        {field('financial_responsible_email', 'E-mail do responsável', { type: 'email', inputMode: 'email', maxLength: 255 })}
                    </div>
                )}
            </div>

            <p className="ppr-consent">
                Ao enviar, você autoriza {clinicName || 'a clínica'} a usar estes dados para o seu atendimento.
            </p>

            <button type="submit" className="ps-btn ps-btn--primary ps-btn--full" disabled={submitting}>
                {submitting ? 'Enviando...' : 'Enviar cadastro'}
            </button>
        </form>
    );
}
